import { chatsApi } from '@/lib/api';
import { useChatStore } from '@/store';
import { restoreLastUserPromptToComposer } from '@/lib/restoreComposerPrompt';

export const TEMP_CHAT_CLEANUP_DELAY_MS = 500;

let activeChatRouteId = null;
const pendingCleanups = new Map();

export function getActiveChatRouteId() {
  return activeChatRouteId;
}

export function setActiveChatRouteId(id) {
  const next = id ? String(id) : null;
  activeChatRouteId = next;
  if (next && pendingCleanups.has(next)) {
    clearTimeout(pendingCleanups.get(next));
    pendingCleanups.delete(next);
  }
}

export function shouldCleanupTemporaryChat({
  conversationId,
  activeRouteId = activeChatRouteId,
  chat,
} = {}) {
  if (!conversationId) return false;
  if (chat?.flags?.isTemporary !== true) return false;
  if (activeRouteId && String(activeRouteId) === String(conversationId)) return false;
  return true;
}

export function cleanupTemporaryChatById(conversationId, deleteChat, options = {}) {
  if (!conversationId) return;
  const delayMs = options.delayMs ?? TEMP_CHAT_CLEANUP_DELAY_MS;
  const existing = pendingCleanups.get(conversationId);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(() => {
    pendingCleanups.delete(conversationId);
    void (async () => {
      try {
        const chat = useChatStore.getState().chats.find(c => c.id === conversationId);
        if (!shouldCleanupTemporaryChat({
          conversationId,
          activeRouteId: getActiveChatRouteId(),
          chat,
        })) return;
        const hasAssistant = (chat.messages || []).some((message) => (
          message?.role === 'assistant' && !message?.flags?.isDeleted
        ));
        const inferencing = chat.isInferencing || chat.isInferencingWaiting;
        if (inferencing || !hasAssistant) {
          restoreLastUserPromptToComposer(conversationId, { contextId: 'new' });
        }
        useChatStore.getState().markTemporaryChatDeleted(conversationId);
        if (typeof deleteChat === 'function') deleteChat(conversationId);
        await chatsApi.deleteTemporaryChat(conversationId);
      } catch (e) {
        console.error('Temporary chat cleanup failed:', e);
      }
    })();
  }, delayMs);

  pendingCleanups.set(conversationId, timer);
}

export function resetTemporaryChatCleanupForTests() {
  pendingCleanups.forEach((timer) => clearTimeout(timer));
  pendingCleanups.clear();
  activeChatRouteId = null;
}
